/**
 * Patient Emergency Consultation — request urgent doctor consultation.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientAPI } from '../../services/api';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';

const EmergencyConsultation = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ symptoms: '', chronic_condition: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.symptoms) {
      toast.error('Please describe your symptoms.');
      return;
    }
    setSubmitting(true);
    try {
      await patientAPI.requestEmergency({ symptoms: form.symptoms, severity: form.chronic_condition ? 'critical' : 'high', chronic_condition: form.chronic_condition });
      toast.success('Emergency request sent! A doctor will respond shortly.');
      navigate('/patient/appointments');
    } catch (err) {
      toast.error(typeof err === 'string' ? err : (err?.error || err?.message || 'Failed to send emergency request.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout title="Emergency" role="patient" showBack>
      <div className="p-4 pb-24">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-red-200 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h2 className="font-bold text-red-800">Emergency Consultation</h2>
              <p className="text-sm text-red-700 mt-1">
                This will immediately notify all available doctors. Use only for genuine medical emergencies. 
                For life-threatening situations, please also call <strong>999</strong> or <strong>112</strong>.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="card space-y-4">
          <div>
            <label className="label">Describe Your Emergency *</label>
            <textarea
              value={form.symptoms}
              onChange={e => setForm(f => ({ ...f, symptoms: e.target.value }))}
              className="input-field"
              rows="4"
              placeholder="Describe your symptoms in detail..."
              required
            />
          </div>

          <div>
            <label className="label">Related Chronic Condition</label>
            <select
              value={form.chronic_condition}
              onChange={e => setForm(f => ({ ...f, chronic_condition: e.target.value }))}
              className="input-field"
            >
              <option value="">Select if applicable</option>
              <option value="diabetes">Diabetes</option>
              <option value="hypertension">Hypertension</option>
              <option value="asthma">Asthma</option>
              <option value="heart_disease">Heart Disease</option>
              <option value="kidney_disease">Kidney Disease</option>
              <option value="other">Other</option>
            </select>
          </div>

          <button type="submit" disabled={submitting} className="btn-danger w-full flex items-center justify-center gap-2">
            {submitting && <div className="spinner w-5 h-5 border-2"></div>}
            🚨 Request Emergency Consultation
          </button>
        </form>
      </div>
    </Layout>
  );
};

export default EmergencyConsultation;
